import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Avatar, Box, CircularProgress, Fade, Stack, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import { alpha } from '@mui/material/styles';

const ReactivationModal = ({
    show,
    close,
    handleReactivate,
    endDate,
    firstName,
    lastName,
    isLoading,
}) => {
    return (
        <Dialog
            open={show}
            onClose={(event, reason) => {
                if (reason !== "backdropClick") {
                    close(event, reason);
                }
            }}
            TransitionComponent={Fade}
            transitionDuration={400}
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    overflow: 'hidden',
                    maxWidth: '500px',
                    width: '100%',
                    m: { xs: 1, sm: 2 }, // Responsive margin
                }
            }}
            sx={{ backdropFilter: "blur(5px)" }}
            aria-labelledby="reactivation-modal-title"
            aria-describedby="reactivation-modal-description"
        >
            {/* Top accent bar */}
            <Box sx={{
                height: 4,
                width: '100%',
                background: 'linear-gradient(90deg, #61892f 0%, #4f7027 100%)'
            }} />

            <Box sx={{ pt: 4, pb: 2, px: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Avatar sx={{
                        bgcolor: alpha('#f44336', 0.1),
                        color: '#f44336',
                        mr: 2
                    }}>
                        <WarningAmberIcon />
                    </Avatar>
                    <Typography
                        id="reactivation-modal-title"
                        variant="h5"
                        fontWeight={700}
                        color="text.primary"
                    >
                        Account Reactivation
                    </Typography>
                </Box>

                <Typography variant="body1" gutterBottom sx={{ mb: 2 }}>
                    Hey, <b>{firstName} {lastName}</b>
                </Typography>

                <Box
                    sx={{
                        p: 2.5,
                        bgcolor: 'rgba(0,0,0,0.02)',
                        borderRadius: 1.5,
                        border: '1px solid rgba(0,0,0,0.08)',
                        mb: 3,
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1.5
                    }}
                >
                    <InfoOutlinedIcon sx={{ color: '#61892f', mt: 0.5 }} />
                    <Box>
                        <Typography
                            id="reactivation-modal-description"
                            variant="body2"
                            sx={{ lineHeight: 1.6, color: 'black', fontSize: "13px" }}
                        >
                            Our automated system has identified prolonged inactivity on your Deloitte GitHub Enterprise account.
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ mt: 2, lineHeight: 1.6, color: 'black', fontSize: "13px" }}
                        >
                            To continue using your account, please confirm your access by <b>{endDate}</b>. Simply click <b>Confirm</b>  to keep your account active.
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 1 }}>
                    <Button
                        variant="outlined"
                        onClick={close}
                        disabled={isLoading}
                        sx={{
                            px: 3,
                            py: 1,
                            borderColor: '#bdbdbd',
                            color: '#555',
                            fontWeight: 500,
                            bgcolor: 'white',
                            '&:hover': {
                                borderColor: '#757575',
                                bgcolor: 'rgba(0,0,0,0.03)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleReactivate}
                        disabled={isLoading}
                        sx={{
                            px: 3,
                            py: 1,
                            bgcolor: '#4f7027',
                            color: '#fff',
                            fontWeight: 600,
                            boxShadow: 'none',
                            '&:hover': {
                                bgcolor: '#35501a'
                            },
                            '&.Mui-disabled': {
                                bgcolor: 'rgba(0,0,0,0.12)',
                                color: '#fff'
                            }
                        }}
                    >
                        {isLoading ? (
                            <>
                                <CircularProgress size={16} sx={{ mr: 1, color: 'white' }} />
                                Reactivating...
                            </>
                        ) : (
                            "Confirm"
                        )}
                    </Button>
                </Box>
            </Box>
        </Dialog>
    );
};

export default ReactivationModal;